import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Editor as TipTapEditor, useEditor } from "@tiptap/react";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  InboxSidebarOptionKey,
  OutreachContactsContext,
  ScheduledType,
} from "contexts/OutreachContacts";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Email, GmailThread, MessageStatus } from "schemas/dashboard";
import { routes } from "schemas/routes";

import Editor from "components/Editor";
import { extensions } from "components/Editor/utils";
import StyledButton from "components/Styled/Button";
import { getPageName } from "constants/trackingProps";
import AttachmentManager from "features/Influencer/ContactList/AttachmentManager";
import RecipientSelect, {
  ContactSelection,
} from "features/Influencer/ContactList/OutreachContactDrawer/ScheduledEmail/RecipientSelect";
import SelectTemplate from "features/Influencer/ContactList/OutreachContactDrawer/ScheduledEmail/SelectTemplate";
import { TemplateContentType } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { sanitize } from "utils/sanitize";
import { hideEmail } from "utils/string";
import { formatEpochTimestamp } from "utils/time";
import { makeDeepCopy } from "utils/updateLocalState";

import styles from "./styles";

interface EmailDraftComponentProps {
  gmailThread: GmailThread | undefined;
  setGmailThread: Dispatch<SetStateAction<GmailThread | undefined>>;
}

export default function EmailDraftComponentScheduled({
  gmailThread,
  setGmailThread,
}: EmailDraftComponentProps) {
  const {
    currentContact,
    setCurrentContact,
    currentContactScheduledEmails,
    setCurrentContactScheduledEmails,
    setContacts,
    contacts,
    setScheduledTotals,
    fetchContactsParams,
    handleFetchContacts,
    fetchEmailThreads,
    fetchContact,
    fetchScheduledEmails,
    setTotals,
  } = useContext(OutreachContactsContext);
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const fromCampaign = Boolean(campaignId);

  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const [emailDraft, setEmailDraft] = useState<Email | null>(null);
  const body = sanitize(emailDraft?.body || "");
  const [sendLoading, setSendLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [attachmentDialogOpen, setAttachmentDialogOpen] =
    useState<boolean>(false);
  const [previousTemplateId, setPreviousTemplateId] = useState<number | null>(
    null,
  );
  const [contactSelection, setContactSelection] =
    useState<ContactSelection | null>(null);

  const pendingOutreach = currentContactScheduledEmails?.find(
    (email) => email.contentType === TemplateContentType.OUTREACH,
  );
  const hasPendingOutreach = Boolean(pendingOutreach);
  const isOutreachDraft =
    emailDraft?.contentType === TemplateContentType.OUTREACH;
  const canChangeRecipient = Boolean(
    isOutreachDraft && pendingOutreach && currentContact?.bentoBrandId,
  );
  const recipientHint = !currentContact?.bentoBrandId
    ? null
    : !hasPendingOutreach
      ? "Outreach sequences that have started cannot change contact"
      : !isOutreachDraft
        ? "To change the recipient, update it on the Outreach tab"
        : null;

  const handleSelectContact = (selection: ContactSelection) => {
    setContactSelection(selection);
    setEmailDraft((prev) => (prev ? { ...prev, isChanged: true } : prev));
  };

  const handleSavePendingEmail = async () => {
    try {
      setSaveLoading(true);
      const { email } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/emails/${emailDraft?.id}`,
        "PATCH",
        {},
        {
          overrideSubject: emailDraft?.subject,
          overrideBody: emailDraft?.body,
          overrideAttachment: emailDraft?.attachment,
          overrideTemplateId: emailDraft?.templateId,
          previousTemplateId,
          bentoContactId: contactSelection?.bentoContactId,
          newContact: contactSelection?.newContact,
        },
      );
      if (contactSelection) {
        setAlert("Saved and recipient updated", "success");
        setContactSelection(null);
        handleFetchContacts({ ...fetchContactsParams, page: 1 });
        navigate(`/${routes.inboxScheduled}/${email.outreachContactId}`, {
          replace: true,
        });
        return;
      }
      setPreviousTemplateId(emailDraft?.templateId || null);
      setCurrentContactScheduledEmails((prev) => {
        const index = prev.findIndex((e) => e.id === email.id);
        if (index !== -1) {
          const newEmails = [...prev];
          newEmails[index] = email;
          return newEmails;
        }
        return prev;
      });
      setContacts((prev) => {
        const index = prev.findIndex((c) => c.id === email.outreachContactId);
        if (index !== -1) {
          const matchedContact = prev[index];
          const newEmails = makeDeepCopy(matchedContact.emails || []);
          const emailIndex = newEmails.findIndex(
            (e: Email) => e.id === email.id,
          );
          if (emailIndex !== -1) {
            newEmails[emailIndex] = email;
          }
          prev[index].emails = newEmails;
        }
        return prev;
      });

      setAlert("Email draft saved", "success");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSendPendingEmail = async () => {
    try {
      setSendLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/emails/${emailDraft?.id}/send`,
        "POST",
        {},
        {
          overrideSubject: emailDraft?.subject,
          overrideBody: emailDraft?.body,
          overrideAttachment: emailDraft?.attachment,
          sendFrom: getPageName(),
          bentoContactId: contactSelection?.bentoContactId,
          newContact: contactSelection?.newContact,
        },
      );
      if (res.email.status === MessageStatus.COMPLETED) {
        setAlert("Successfully sent scheduled email!", "success");
        setContactSelection(null);
        updateCountPostEmail();
      } else {
        setAlert(
          "Email cancelled, most likely because you have exceeded follow up emails allowed or your contact has already responded",
          "success",
        );
      }
    } catch (error) {
      setAlert(
        "Something went wrong, unable to send scheduled email. Please reload and retry again.",
        "error",
      );
    } finally {
      setSendLoading(false);
    }
  };

  const handleCancelMessage = async () => {
    try {
      setDeleteLoading(true);
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/emails/${emailDraft?.id}/cancel`,
        "POST",
      );
      setAlert("Successfully deleted email", "success");
      const noEmailLeft =
        currentContactScheduledEmails?.length === 1 ||
        emailDraft?.contentType === TemplateContentType.OUTREACH;

      setCurrentContactScheduledEmails((prev) => {
        return prev.filter((email) => email.id !== emailDraft?.id);
      });
      if (noEmailLeft) {
        navigateToNextContact();
        decreaseEmailFromList();
      }
    } catch (error) {
      setAlert(
        "Something went wrong, unable to cancel email. Please reload and retry again.",
        "error",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const updateCountPostEmail = () => {
    if (!emailDraft?.outreachContactId) return;
    const outreachIsSent =
      emailDraft?.contentType === TemplateContentType.OUTREACH;
    if (outreachIsSent) {
      navigateToNextContact();
    } else {
      if (gmailThread?.messages && gmailThread?.messages?.length > 0) {
        const copy = makeDeepCopy(
          gmailThread.messages?.[gmailThread.messages?.length - 1],
        );
        copy["body"] = emailDraft?.body;
        copy["attachment"] = emailDraft?.attachment;
        setGmailThread((prev) => {
          if (prev) {
            return {
              ...prev,
              messages: [...(prev.messages || []), copy],
            };
          }
          return prev;
        });
      }
      fetchScheduledEmails(emailDraft?.outreachContactId);
      fetchEmailThreads(emailDraft?.outreachContactId, true);
      fetchContact(emailDraft?.outreachContactId);
    }
    decreaseEmailFromList();
  };

  const navigateToNextContact = () => {
    if (fromCampaign) {
      navigate(`/${routes.campaignView}/${campaignId}`, { replace: true });
      return;
    }
    const index = contacts?.findIndex(
      (x) => x.id === emailDraft?.outreachContactId,
    );
    let nextContact = null;
    if (index !== -1) {
      nextContact = contacts?.[index + 1] || contacts?.[index - 1];
    } else {
      nextContact = contacts?.[0];
    }
    if (nextContact) {
      navigate(`/${routes.inboxScheduled}/${nextContact.id}`, {
        replace: true,
      });
    } else {
      setCurrentContact(null);
      navigate(`/${routes.inboxScheduled}`, { replace: true });
    }
  };

  const decreaseEmailFromList = () => {
    setContacts((prev) => {
      return prev.filter(
        (contact) => contact.id !== emailDraft?.outreachContactId,
      );
    });
    if (fetchContactsParams.scheduledType === ScheduledType.outreach) {
      setScheduledTotals((prev) => ({
        ...prev,
        [ScheduledType.outreach]: (prev[ScheduledType.outreach] || 1) - 1,
      }));
    } else {
      setScheduledTotals((prev) => ({
        ...prev,
        [ScheduledType.followUp]: (prev[ScheduledType.followUp] || 1) - 1,
      }));
    }
    setTotals((prev) => ({
      ...prev,
      [InboxSidebarOptionKey.scheduled]:
        (prev[InboxSidebarOptionKey.scheduled] || 1) - 1,
    }));
  };

  const handleChangeTemplate = async (templateId: number) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach-contacts/${emailDraft?.outreachContactId}/preview`,
        "POST",
        {},
        {
          templateId,
        },
      );
      setEmailDraft((prev) => {
        if (prev && editor) {
          let copy = makeDeepCopy(prev);
          if (res.preview.subject) {
            copy.subject = res.preview.subject;
          }
          editor.commands.setContent(res.preview.body);
          copy.isChanged = true;
          copy.body = res.preview.body;
          copy.attachment = res.preview.attachment;
          copy.missingValues = res.preview.missingValues || [];
          copy.templateId = templateId;
          return copy;
        }
        return prev;
      });
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const formatContact = () => {
    let formattedContact = "";
    if (currentContact?.contactTitle && currentContact?.contactName) {
      formattedContact =
        currentContact?.contactName + ", " + currentContact?.contactTitle;
    } else if (currentContact?.contactName) {
      formattedContact = currentContact.contactName + " ";
    }
    if (currentContact?.email) {
      return `${formattedContact.trim()} <${hideEmail(currentContact?.email)}>`;
    }
  };

  const editor = useEditor(
    {
      extensions: extensions(),
      content: body,
      onUpdate({ editor }) {
        setEmailDraft((prev) => {
          if (prev) {
            return { ...prev, isChanged: true, body: editor.getHTML() };
          }
          return prev;
        });
      },
    },
    [],
  ) as TipTapEditor;

  useEffect(() => {
    if (
      editor &&
      emailDraft?.body &&
      emailDraft?.body?.length > 0 &&
      editor.getHTML() !== emailDraft?.body
    ) {
      editor.commands.setContent(emailDraft?.body, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor?.getHTML(), emailDraft?.body]);

  useEffect(() => {
    if (currentContactScheduledEmails?.[0]) {
      setEmailDraft({ ...currentContactScheduledEmails?.[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentContact?.id, currentContactScheduledEmails]);

  useEffect(() => {
    if (emailDraft?.templateId) {
      setPreviousTemplateId(emailDraft.templateId);
    }
  }, [emailDraft?.templateId]);

  useEffect(() => {
    setContactSelection(null);
  }, [emailDraft?.id]);

  if (!currentContact) {
    return <></>;
  }

  return (
    <Box>
      <Grid container sx={styles.scheduledContainer}>
        {currentContactScheduledEmails?.map((draft, index) => {
          if (
            draft?.contentType === TemplateContentType.OUTREACH ||
            draft?.contentType === TemplateContentType.INBOX
          ) {
            return (
              <Button
                key={index}
                disableElevation
                sx={[
                  styles.buttonTab,
                  styles.firstBorder,
                  emailDraft?.id === draft?.id ? styles.active : {},
                  currentContactScheduledEmails?.length === 1
                    ? styles.lastBorder
                    : {},
                ]}
                onClick={() => setEmailDraft(draft)}
              >
                Outreach
              </Button>
            );
          } else {
            return (
              <Button
                key={index}
                sx={[
                  styles.buttonTab,
                  index === (currentContactScheduledEmails?.length || 1) - 1
                    ? styles.lastBorder
                    : {},
                  emailDraft?.id === draft?.id ? styles.active : {},
                  !hasPendingOutreach && index === 0 ? styles.firstBorder : {},
                ]}
                onClick={() => setEmailDraft(draft)}
              >
                Follow-up {hasPendingOutreach ? index : index + 1}
              </Button>
            );
          }
        })}
      </Grid>

      <Grid container sx={styles.subjectContainer} alignItems={"center"}>
        <Grid item>
          <Typography component="span" sx={styles.annotation}>
            To:
          </Typography>
        </Grid>
        <Grid item xs sx={{ ml: 2 }}>
          {canChangeRecipient && currentContact?.bentoBrandId ? (
            <RecipientSelect
              bentoBrandId={currentContact.bentoBrandId}
              currentBentoContactId={currentContact.bentoContactId}
              currentEmail={currentContact.email}
              selection={contactSelection}
              onSelect={handleSelectContact}
            />
          ) : recipientHint ? (
            <Tooltip title={recipientHint}>
              <Typography component={"span"} sx={[styles.toContact, { ml: 0 }]}>
                {formatContact()}
              </Typography>
            </Tooltip>
          ) : (
            <Typography component={"span"} sx={[styles.toContact, { ml: 0 }]}>
              {formatContact()}
            </Typography>
          )}
        </Grid>
      </Grid>

      {(emailDraft?.contentType === TemplateContentType.OUTREACH ||
        emailDraft?.contentType === TemplateContentType.INBOX) && (
        <Grid container sx={styles.subjectContainer} alignItems={"center"}>
          <Grid item>
            <Typography component="span" sx={styles.annotation}>
              Subject:
            </Typography>
          </Grid>

          <Grid item xs>
            <TextField
              size="small"
              value={emailDraft?.subject || ""}
              onChange={(e) => {
                const { value } = e.target;
                setEmailDraft((prev) => {
                  if (prev) {
                    return { ...prev, isChanged: true, subject: value };
                  }
                  return prev;
                });
              }}
              fullWidth
              sx={styles.noBorder}
            />
          </Grid>
        </Grid>
      )}

      <Box
        sx={[
          styles.subjectContainer,
          styles.editorContainer(
            emailDraft?.contentType || TemplateContentType.OUTREACH,
          ),
        ]}
      >
        <Editor
          editor={editor}
          allowImage={true}
          showVariableError
          hasBorder={false}
          allowAttachment={true}
          openAttachmentManager={() => {
            setAttachmentDialogOpen(true);
          }}
          attachment={emailDraft?.attachment}
          removeAttachment={() => {
            setEmailDraft((prev) => {
              if (prev) {
                return { ...prev, isChanged: true, attachment: "" };
              }
              return prev;
            });
          }}
          editorDecoratorBelowMenuBar={
            <>
              {emailDraft && (
                <SelectTemplate
                  scheduledEmail={emailDraft}
                  handleChangeTemplate={handleChangeTemplate}
                />
              )}
            </>
          }
        />
      </Box>

      <Grid container item xs={12} gap={1} sx={styles.scheduledBottomAction}>
        <Grid item xs={12}>
          {emailDraft?.sendAt && (
            <Box sx={{ fontSize: 12 }}>
              <em>
                Scheduled for{" "}
                {formatEpochTimestamp({
                  epochTimestamp: emailDraft?.sendAt,
                  fullDate: true,
                })}
              </em>
            </Box>
          )}
        </Grid>
        <StyledButton
          disableElevation
          variant="contained"
          onClick={handleSavePendingEmail}
          loading={saveLoading}
        >
          Save Changes
        </StyledButton>

        {currentContactScheduledEmails?.[0]?.id === emailDraft?.id && (
          <StyledButton onClick={handleSendPendingEmail} loading={sendLoading}>
            Send Now
          </StyledButton>
        )}
        <StyledButton loading={deleteLoading} onClick={handleCancelMessage}>
          Delete
        </StyledButton>
      </Grid>

      <AttachmentManager
        enteredSelection={(attachment: string) =>
          setEmailDraft((prev) => {
            if (prev) {
              return { ...prev, isChanged: true, attachment };
            }
            return prev;
          })
        }
        open={attachmentDialogOpen}
        setOpen={setAttachmentDialogOpen}
      />
    </Box>
  );
}
